import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createTournament, getAllGames, getTournament, updateTournament } from "../api/api_funcs";
import { useRemoteControl, useRemotePage } from "../remote/RemoteControlContext";
import { useRemoteSelectionScroll } from "../remote/useRemoteSelectionScroll";

const initialForm = {
  name: "",
  gameIds: [],
  scoreMode: "Cumulative",
  teamMode: "Individual",
  motion: false,
};

function ChoiceButton({ active, title, description, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group rounded-2xl border p-4 text-left transition-all duration-200 ${
        active
          ? "border-fuchsia-400/80 bg-fuchsia-500/15 shadow-lg shadow-fuchsia-500/10 ring-1 ring-fuchsia-400/40"
          : "border-white/10 bg-slate-950/45 hover:-translate-y-0.5 hover:border-indigo-400/50 hover:bg-indigo-500/10"
      }`}
    >
      <span className="flex items-center gap-3 font-semibold text-white">
        <span className={`h-3 w-3 rounded-full ${active ? "bg-fuchsia-400 shadow-[0_0_14px_#e879f9]" : "bg-slate-600 group-hover:bg-indigo-400"}`} />
        {title}
      </span>
      <span className="mt-2 block text-sm leading-5 text-slate-400">{description}</span>
    </button>
  );
}

export default function TournamentBuilderPage() {
  const { remoteActive } = useRemoteControl();
  const { tournamentId } = useParams();
  const [games, setGames] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [focusedGameIndex, setFocusedGameIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getAllGames({ playable: true }),
      tournamentId ? getTournament(tournamentId) : Promise.resolve(null),
    ])
      .then(([gameItems, tournament]) => {
        const playableGames = gameItems.filter((game) => game.user_has_access);
        const playableIds = new Set(playableGames.map((game) => game.id));
        setGames(playableGames);
        if (tournament) {
          setForm({
            name: tournament.name,
            gameIds: (tournament.games_order_ids || []).filter((id) => playableIds.has(id)),
            scoreMode: tournament.score_mode,
            teamMode: tournament.team_mode,
            motion: tournament.is_motion_tournament,
          });
        }
      })
      .catch((requestError) => setError(requestError?.response?.data?.detail || requestError.message))
      .finally(() => setLoading(false));
  }, [tournamentId]);

  const filteredGames = useMemo(() => {
    const query = search.trim().toLowerCase();
    return games.filter((game) => {
      if (form.motion && !game.is_motion_game) return false;
      if (!query) return true;
      const searchable = [game.title, ...(game.tags || []), ...(game.genres || [])]
        .join(" ")
        .toLowerCase();
      return searchable.includes(query);
    });
  }, [form.motion, games, search]);

  const selectedGames = useMemo(
    () => form.gameIds.map((id) => games.find((game) => game.id === id)).filter(Boolean),
    [form.gameIds, games]
  );
  const focusedGame = filteredGames[focusedGameIndex] || null;
  const gameGridRef = useRemoteSelectionScroll(remoteActive, focusedGame?.id ?? null);

  useEffect(() => {
    setFocusedGameIndex((current) => Math.max(0, Math.min(current, filteredGames.length - 1)));
  }, [filteredGames.length]);

  function toggleGame(id) {
    setForm((current) => ({
      ...current,
      gameIds: current.gameIds.includes(id)
        ? current.gameIds.filter((gameId) => gameId !== id)
        : [...current.gameIds, id],
    }));
  }

  function moveGame(index, direction) {
    setForm((current) => {
      const next = [...current.gameIds];
      const target = index + direction;
      if (target < 0 || target >= next.length) return current;
      [next[index], next[target]] = [next[target], next[index]];
      return { ...current, gameIds: next };
    });
  }

  async function saveTournament() {
    if (!form.name.trim() || !form.gameIds.length || saving) return;
    setSaving(true);
    setError("");
    const payload = {
      name: form.name.trim(),
      time_limit: 0,
      game_ids: form.gameIds,
      games_order_ids: form.gameIds,
      score_mode: form.scoreMode,
      team_mode: form.teamMode,
      is_motion_tournament: form.motion,
    };
    try {
      if (tournamentId) await updateTournament(tournamentId, payload);
      else await createTournament(payload);
      navigate("/tournaments");
    } catch (requestError) {
      setError(JSON.stringify(requestError?.response?.data || requestError.message));
      setSaving(false);
    }
  }

  function submit(event) {
    event.preventDefault();
    saveTournament();
  }

  const moveFocus = (direction) => {
    if (!filteredGames.length) return;
    setFocusedGameIndex((current) => (current + direction + filteredGames.length) % filteredGames.length);
  };
  const focusedSelectedIndex = focusedGame ? form.gameIds.indexOf(focusedGame.id) : -1;

  useRemotePage({
    id: "tournament-builder",
    title: tournamentId ? "Edit tournament" : "Build tournament",
    subtitle: focusedGame?.title || `${selectedGames.length} rounds selected`,
    state: { form, search, focusedGame: focusedGame?.title || "", selectedCount: selectedGames.length, saving },
    sections: [
      { title: "Tournament", controls: [
        { id: "builder.name", type: "text", label: "Name", value: form.name, maxLength: 100, placeholder: "Friday Night Championship" },
        { id: "builder.search", type: "text", label: "Search games", value: search, maxLength: 100, placeholder: "Title, tag or genre" },
        { id: "builder.previous", type: "button", label: "Previous game", disabled: !filteredGames.length },
        { id: "builder.next", type: "button", label: "Next game", disabled: !filteredGames.length },
        { id: "builder.toggle", type: "button", label: focusedSelectedIndex >= 0 ? "Remove game" : "Add game", disabled: !focusedGame },
        { id: "builder.moveUp", type: "button", label: "Move round up", disabled: focusedSelectedIndex <= 0 },
        { id: "builder.moveDown", type: "button", label: "Move round down", disabled: focusedSelectedIndex < 0 || focusedSelectedIndex >= selectedGames.length - 1 },
      ] },
      { title: "Rules", controls: [
        { id: "builder.score", type: "select", label: "Scoring", value: form.scoreMode, options: [{ value: "Cumulative", label: "Cumulative" }, { value: "Elimination", label: "Elimination" }] },
        { id: "builder.team", type: "select", label: "Competition", value: form.teamMode, options: [{ value: "Individual", label: "Individual" }, { value: "Team", label: "Teams" }] },
        { id: "builder.motion", type: "toggle", label: "Motion tournament", value: form.motion },
        { id: "builder.save", type: "button", label: tournamentId ? "Save changes" : "Create tournament", disabled: saving || !form.name.trim() || !form.gameIds.length },
        { id: "builder.cancel", type: "button", label: "Cancel" },
      ] },
    ],
    actions: {
      "builder.name": ({ value }) => setForm((current) => ({ ...current, name: String(value || "").slice(0, 100) })),
      "builder.search": ({ value }) => { setSearch(String(value || "").slice(0, 100)); setFocusedGameIndex(0); },
      "builder.previous": () => moveFocus(-1),
      "builder.next": () => moveFocus(1),
      "builder.toggle": () => focusedGame && toggleGame(focusedGame.id),
      "builder.moveUp": () => focusedSelectedIndex >= 0 && moveGame(focusedSelectedIndex, -1),
      "builder.moveDown": () => focusedSelectedIndex >= 0 && moveGame(focusedSelectedIndex, 1),
      "builder.score": ({ value }) => setForm((current) => ({ ...current, scoreMode: value === "Elimination" ? "Elimination" : "Cumulative" })),
      "builder.team": ({ value }) => setForm((current) => ({ ...current, teamMode: value === "Team" ? "Team" : "Individual" })),
      "builder.motion": ({ value }) => setForm((current) => ({ ...current, motion: Boolean(value), gameIds: [] })),
      "builder.save": saveTournament,
      "builder.cancel": () => navigate("/tournaments"),
    },
  });

  return (
    <main className="relative mx-auto w-full max-w-7xl flex-1 px-4 py-10 text-left sm:px-6 lg:px-8">
      <div className="pointer-events-none absolute left-1/2 top-20 -z-10 h-96 w-96 -translate-x-1/2 rounded-full bg-fuchsia-600/15 blur-3xl" />

      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-fuchsia-300/20 bg-fuchsia-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-fuchsia-200">
            Tournament studio
          </div>
          <h1 className="text-4xl font-black tracking-tight text-white sm:text-5xl">
            {tournamentId ? "Refine your tournament" : "Build your tournament"}
          </h1>
          <p className="mt-3 max-w-2xl text-base text-slate-300">
            Choose from your playable games, arrange the rounds, and decide how the winner is crowned.
          </p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-3 text-center backdrop-blur">
          <div className="text-2xl font-black text-fuchsia-300">{selectedGames.length}</div>
          <div className="text-xs uppercase tracking-wider text-slate-400">Rounds selected</div>
        </div>
      </div>

      <form className="space-y-7" onSubmit={submit}>
        <section className="rounded-3xl border border-white/10 bg-slate-900/70 p-5 shadow-2xl shadow-black/20 backdrop-blur-xl sm:p-7">
          <label className="block text-sm font-semibold uppercase tracking-[0.18em] text-indigo-200" htmlFor="tournament-name">
            Tournament name
          </label>
          <input
            id="tournament-name"
            className="mt-3 w-full rounded-2xl border border-white/10 bg-slate-950/70 px-5 py-4 text-lg font-semibold text-white outline-none transition placeholder:text-slate-600 focus:border-fuchsia-400/70 focus:ring-4 focus:ring-fuchsia-500/10"
            placeholder="Friday Night Championship"
            required
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
        </section>

        <div className="grid gap-7 lg:grid-cols-[minmax(0,1fr)_360px]">
          <section className="rounded-3xl border border-white/10 bg-slate-900/70 p-5 shadow-2xl shadow-black/20 backdrop-blur-xl sm:p-7">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">Choose games</h2>
                <p className="mt-1 text-sm text-slate-400">Only games available in your account are shown.</p>
              </div>
              <label className="relative block sm:w-80">
                <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-slate-500">
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
                </span>
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search title, tag or genre"
                  className="w-full rounded-2xl border border-white/10 bg-slate-950/80 py-3 pl-11 pr-4 text-sm text-white outline-none transition placeholder:text-slate-600 focus:border-indigo-400/70 focus:ring-4 focus:ring-indigo-500/10"
                />
              </label>
            </div>

            <div ref={gameGridRef} className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {filteredGames.map((game, index) => {
                const selected = form.gameIds.includes(game.id);
                return (
                  <button
                    type="button"
                    key={game.id}
                    data-remote-focused={remoteActive && index === focusedGameIndex ? "true" : undefined}
                    onClick={() => toggleGame(game.id)}
                    className={`group relative overflow-hidden rounded-3xl border text-left transition-all duration-200 ${
                      selected
                        ? "-translate-y-1 border-fuchsia-400/80 bg-fuchsia-500/15 shadow-xl shadow-fuchsia-500/15 ring-1 ring-fuchsia-400/40"
                        : remoteActive && index === focusedGameIndex
                          ? "-translate-y-1 border-cyan-300 bg-cyan-500/10 ring-4 ring-cyan-300/20"
                          : "border-white/10 bg-slate-950/55 hover:-translate-y-1 hover:border-indigo-400/60 hover:shadow-xl hover:shadow-indigo-500/10"
                    }`}
                  >
                    <div className="relative h-32 overflow-hidden bg-gradient-to-br from-indigo-700/40 to-fuchsia-700/30">
                      {game.cover_image && <img src={game.cover_image} alt="" className="h-full w-full object-cover transition duration-300 group-hover:scale-105" />}
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
                      <span className={`absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full border backdrop-blur ${selected ? "border-fuchsia-200/70 bg-fuchsia-500 text-white" : "border-white/30 bg-slate-950/60 text-transparent"}`}>
                        ✓
                      </span>
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-white">{game.title}</h3>
                      <p className="mt-1 text-xs text-slate-400">{game.min_players}–{game.max_players || "∞"} players · {game.difficulty}</p>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {(game.tags || []).slice(0, 3).map((tag) => <span key={tag} className="rounded-full bg-white/5 px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400">{tag}</span>)}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {!loading && !filteredGames.length && (
              <div className="mt-6 rounded-3xl border border-dashed border-white/15 bg-slate-950/40 px-6 py-12 text-center">
                <div className="text-lg font-semibold text-slate-300">No playable games found</div>
                <p className="mt-2 text-sm text-slate-500">Try another search or switch the motion-game filter.</p>
              </div>
            )}
            {loading && <div className="mt-6 animate-pulse rounded-3xl bg-white/5 py-16 text-center text-slate-500">Loading your games…</div>}
          </section>

          <aside className="h-fit rounded-3xl border border-white/10 bg-slate-900/80 p-5 shadow-2xl shadow-black/20 backdrop-blur-xl lg:sticky lg:top-6">
            <div className="flex items-center justify-between">
              <div><h2 className="text-xl font-bold text-white">Play order</h2><p className="mt-1 text-xs text-slate-400">First game starts at the top.</p></div>
              <span className="rounded-full bg-indigo-500/15 px-3 py-1 text-xs font-semibold text-indigo-200">{selectedGames.length}</span>
            </div>
            <div className="mt-5 space-y-2">
              {selectedGames.map((game, index) => (
                <div key={game.id} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-slate-950/55 p-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-sm font-black text-white">{index + 1}</span>
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-100">{game.title}</span>
                  <div className="flex gap-1">
                    <button type="button" aria-label={`Move ${game.title} up`} disabled={index === 0} onClick={() => moveGame(index, -1)} className="rounded-lg p-1.5 text-slate-400 transition hover:bg-white/10 hover:text-white disabled:opacity-20">▲</button>
                    <button type="button" aria-label={`Move ${game.title} down`} disabled={index === selectedGames.length - 1} onClick={() => moveGame(index, 1)} className="rounded-lg p-1.5 text-slate-400 transition hover:bg-white/10 hover:text-white disabled:opacity-20">▼</button>
                  </div>
                </div>
              ))}
              {!selectedGames.length && <div className="rounded-2xl border border-dashed border-white/15 px-4 py-9 text-center text-sm text-slate-500">Select games to build the round order.</div>}
            </div>
          </aside>
        </div>

        <section className="rounded-3xl border border-white/10 bg-slate-900/70 p-5 shadow-2xl shadow-black/20 backdrop-blur-xl sm:p-7">
          <h2 className="text-2xl font-bold text-white">Tournament rules</h2>
          <div className="mt-5 grid gap-6 lg:grid-cols-2">
            <div><h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-400">Scoring</h3><div className="grid gap-3 sm:grid-cols-2"><ChoiceButton active={form.scoreMode === "Cumulative"} title="Cumulative" description="Every round contributes to the final total." onClick={() => setForm({ ...form, scoreMode: "Cumulative" })} /><ChoiceButton active={form.scoreMode === "Elimination"} title="Elimination" description="The lowest scorer leaves after each round." onClick={() => setForm({ ...form, scoreMode: "Elimination" })} /></div></div>
            <div><h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-400">Competition</h3><div className="grid gap-3 sm:grid-cols-2"><ChoiceButton active={form.teamMode === "Individual"} title="Individual" description="Every player competes for their own score." onClick={() => setForm({ ...form, teamMode: "Individual" })} /><ChoiceButton active={form.teamMode === "Team"} title="Teams" description="Players choose one of ten color teams." onClick={() => setForm({ ...form, teamMode: "Team" })} /></div></div>
          </div>

          <button
            type="button"
            onClick={() => setForm((current) => ({ ...current, motion: !current.motion, gameIds: [] }))}
            className={`mt-6 flex w-full items-center gap-4 rounded-2xl border p-4 text-left transition ${form.motion ? "border-cyan-400/60 bg-cyan-500/10" : "border-white/10 bg-slate-950/45 hover:border-cyan-400/40"}`}
          >
            <span className={`relative h-7 w-12 shrink-0 rounded-full transition ${form.motion ? "bg-cyan-500" : "bg-slate-700"}`}><span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition ${form.motion ? "left-6" : "left-1"}`} /></span>
            <span><strong className="block text-white">Motion tournament</strong><span className="mt-1 block text-sm text-slate-400">Use camera-based games with up to four players per device.</span></span>
          </button>
        </section>

        {error && <div className="rounded-2xl border border-red-400/30 bg-red-500/10 px-5 py-4 text-red-200">{error}</div>}

        <div className="flex flex-col-reverse gap-3 rounded-3xl border border-white/10 bg-slate-900/80 p-4 shadow-2xl backdrop-blur sm:flex-row sm:items-center sm:justify-between">
          <button type="button" onClick={() => navigate("/tournaments")} className="rounded-2xl px-5 py-3 font-semibold text-slate-300 transition hover:bg-white/5 hover:text-white">Cancel</button>
          <button className="rounded-2xl bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500 px-8 py-3.5 font-bold text-white shadow-xl shadow-fuchsia-500/20 transition hover:-translate-y-0.5 hover:shadow-fuchsia-500/30 disabled:cursor-not-allowed disabled:opacity-40" disabled={saving || !form.gameIds.length || !form.name.trim()}>{saving ? "Saving…" : tournamentId ? "Save changes" : "Create tournament"}</button>
        </div>
      </form>
    </main>
  );
}
